/**
 * **Which screens belong to which kind of profile** (R4; Thomas, 2026-10-08: D81, D84, D92).
 *
 * A person's own profile, Personal, holds what is theirs: the reason they open Autho, the accounts they connect and
 * the people those accounts are about. A company's profile, an Org, holds the company: its structure, its books,
 * its bank, its documents and the questions they raise. Connected accounts, Audit, Agents, Engine and Settings are
 * in both, because each profile has its own (A8).
 *
 * One table, read by the menu, the command palette and the route guard alike, so the three cannot disagree about
 * where a screen lives: a screen the menu hides is a screen the palette does not offer and a link does not open.
 */
export type ProfileKind = 'me' | 'org';

/** Where a profile opens: a person's on what needs them, a company's on its structure. */
export const LANDING = { me: '/attention', org: '/structure' } as const;

/**
 * The screens of one kind only, by the first segment of their path; a deeper path (an entity under Structure)
 * belongs where its first segment does. **Bank and Clarify are the company's** and Relationships the person's:
 * until R4 the three were in both menus, and a person's own profile showed a bank it could never connect.
 */
const ONLY: Readonly<Record<string, ProfileKind>> = {
  '/attention': 'me',
  '/relationships': 'me',
  // Who a person is and what they own and owe, typed by hand (Personal step 3).
  '/profile': 'me',
  '/finances': 'me',
  // What a person works towards, and their own Todoist list read live (Personal step 4).
  '/goals': 'me',
  // The Record: a person's own mail, calendar and files (Personal step 2). An Org never reads a mailbox (Federation.md).
  '/conversations': 'me',
  '/calendar': 'me',
  '/files': 'me',
  '/structure': 'org',
  '/counterparties': 'org',
  '/bank': 'org',
  '/documents': 'org',
  '/clarify': 'org',
  // An agent acts only in a company's profile (`beliefs.remember` is an Org resource), so its approvals live there.
  '/approvals': 'org',
};

/**
 * The kind a path belongs to, or null for a path both kinds have — and for one Autho does not know. **The `/connect/*`
 * addresses belong to no kind, deliberately**: a provider's redirect comes back to whichever profile is open, and the
 * consent screen an assistant is connected on is approved by a person, from either of their profiles (D85).
 */
export function kindOfPath(pathname: string): ProfileKind | null {
  for (const [prefix, kind] of Object.entries(ONLY))
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return kind;
  return null;
}

/** Whether a profile of this kind has the screen at this path. */
export function belongsTo(kind: ProfileKind, pathname: string): boolean {
  const only = kindOfPath(pathname);
  return only === null || only === kind;
}

/**
 * **Whether a listed account offers its first read** (D54): the estimate of what reading it costs, and the approval
 * that starts it. Both are a person's own profile's requests (`POST /api/t/connections/:id/estimate` and
 * `…/approve-sync` are marked `me`), so **a company's profile never offers them**, whatever account it lists: its
 * Todoist is the alert channel, which is never read, and a personal account still listed there is moved, not read
 * (R3). Offered there, the button could only be refused, with advice to switch to a personal profile.
 */
export function offersFirstRead(
  kind: ProfileKind,
  canMutate: boolean,
  account: { state: string; syncApprovedAt: string | null },
): boolean {
  return (
    kind === 'me' && canMutate && account.state !== 'revoked' && account.syncApprovedAt === null
  );
}
